import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { useSettings, trackEvent } from "@/lib/settings";
import LogoMark from "@/components/site/LogoMark";

export default function Header({ onEnquire }) {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({}); // mobile sub-menu expand state
  const [scrolled, setScrolled] = useState(false);
  const [dim, setDim] = useState(false); // hybrid auto-hide (desktop-only, only over dark hero)
  const dimTimer = useRef(null);
  const loc = useLocation();

  // Pages that don't have a dark hero — header should always be in light mode.
  const isLightPage = loc.pathname.startsWith("/blog");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const menu = (settings?.header_menu || [])
    .filter((m) => m.enabled !== false)
    .map((m) => ({ ...m, children: (m.children || []).filter((c) => c.enabled !== false) }));
  const siteName = settings?.site_name || "Happy Retreats";
  const logoLight = settings?.logo_url;
  const logoDarkProvided = settings?.logo_url_dark;

  const lightMode = scrolled || isLightPage;
  const sourceLogo = lightMode ? logoLight : (logoDarkProvided || logoLight);
  const shouldInvert = !lightMode && !logoDarkProvided;
  const logoHeightClass = scrolled ? "h-12 md:h-16" : "h-16 md:h-24";

  // ── Hybrid auto-hide (desktop only, only while at the top over a dark hero) ──
  // 1) Fully visible for the first 1.5s so users register the nav.
  // 2) Then fades to ~15% opacity so the hero video shines through.
  // 3) Snaps back to 100% for 3s when the cursor enters the top 120px of the screen,
  //    or on any scroll movement.
  const canAutoDim = !lightMode && !open; // never dim when on light pages / scrolled / mobile menu open
  const clearDimTimer = () => { if (dimTimer.current) { clearTimeout(dimTimer.current); dimTimer.current = null; } };
  const wakeUp = () => {
    setDim(false);
    clearDimTimer();
    dimTimer.current = setTimeout(() => setDim(true), 3000);
  };

  useEffect(() => {
    if (!canAutoDim) { setDim(false); clearDimTimer(); return; }
    // Only run on desktop
    const mq = window.matchMedia("(min-width: 1024px)");
    if (!mq.matches) return;

    // Initial dim after 1.5s
    clearDimTimer();
    dimTimer.current = setTimeout(() => setDim(true), 1500);

    const onMove = (e) => { if (e.clientY <= 120) wakeUp(); };
    const onWheel = () => wakeUp();
    const onKey = () => wakeUp();

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("keydown", onKey);
    return () => {
      clearDimTimer();
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAutoDim]);

  const dimmed = dim && canAutoDim;

  return (
    <header
      className={`hr-header fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        lightMode ? "bg-ivory/92 border-b border-ink/10 backdrop-blur-md" : "bg-gradient-to-b from-ink/30 to-transparent"
      } ${dimmed ? "opacity-15 hover:opacity-100" : "opacity-100"}`}
      onMouseEnter={() => canAutoDim && wakeUp()}
      data-testid="site-header"
      data-dimmed={dimmed ? "true" : "false"}
    >
      <div className="container-x">
        {/* Top row: hamburger (mobile) | centered logo | CTA right */}
        <div className={`grid grid-cols-3 items-center transition-all duration-500 ${scrolled ? "h-20 md:h-24" : "h-24 md:h-32"}`}>
          {/* Left: hamburger on mobile, empty on desktop to balance centering */}
          <div className="justify-self-start flex items-center">
            <button
              className={`lg:hidden p-2 -ml-2 ${lightMode ? "text-ink" : "text-ivory"}`}
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Center: brand */}
          <Link to="/" className="justify-self-center flex items-center" data-testid="site-logo">
            {sourceLogo ? (
              <LogoMark
                src={sourceLogo}
                dark={shouldInvert}
                className={`${logoHeightClass} w-auto transition-all duration-500`}
                alt={siteName}
              />
            ) : (
              <div className={`leading-none text-center ${lightMode ? "text-ink" : "text-ivory"}`}>
                <span className="font-display text-3xl md:text-4xl tracking-tight block">{siteName.split(" ")[0]}</span>
                <span className="font-display text-3xl md:text-4xl tracking-tight italic text-clay block">{siteName.split(" ").slice(1).join(" ")}</span>
              </div>
            )}
          </Link>

          {/* Right: CTA */}
          <div className="justify-self-end flex items-center gap-2 lg:gap-4">
            {settings?.contact?.phone && (
              <a
                href={`tel:${settings.contact.phone.replace(/\s+/g, "")}`}
                onClick={() => trackEvent("call_click", { location: "header" })}
                className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-medium transition-colors hover:text-clay ${lightMode ? "text-ink" : "text-ivory"}`}
                data-testid="header-call-btn"
                aria-label="Call us"
              >
                <Phone className="w-4 h-4" />
                <span className="hidden lg:inline">{settings.contact.phone}</span>
              </a>
            )}
            <button
              onClick={onEnquire}
              className={`hidden md:inline-flex items-center px-5 lg:px-7 py-3 text-[10px] lg:text-[11px] uppercase tracking-[0.22em] font-medium transition-colors ${
                lightMode ? "bg-ink text-ivory hover:bg-clay" : "bg-ivory text-ink hover:bg-clay hover:text-ivory"
              }`}
              data-testid="header-enquire-btn"
            >
              Plan a visit
            </button>
          </div>
        </div>

        {/* Nav row — centered below logo on desktop */}
        <nav
          className={`hidden lg:flex items-center justify-center gap-10 transition-all duration-500 ${
            scrolled ? "py-2.5 border-t border-ink/5" : "pb-5"
          }`}
          data-testid="header-nav"
        >
          {menu.map((m, i) => {
            const linkCls = `text-[11px] uppercase tracking-[0.22em] font-medium transition-colors hover:text-clay ${lightMode ? "text-ink" : "text-ivory"}`;
            if (!m.children?.length) {
              return (
                <a key={i} href={m.url} className={linkCls} data-testid={`nav-link-${i}`}>
                  {m.label}
                </a>
              );
            }
            return (
              <div key={i} className="relative group" data-testid={`nav-dropdown-${i}`}>
                {m.url ? (
                  <a href={m.url} className={`${linkCls} inline-flex items-center gap-1.5`} data-testid={`nav-link-${i}`}>
                    {m.label} <ChevronDown className="w-3 h-3 transition-transform duration-200 group-hover:rotate-180" />
                  </a>
                ) : (
                  <span className={`${linkCls} inline-flex items-center gap-1.5 cursor-default`} data-testid={`nav-link-${i}`}>
                    {m.label} <ChevronDown className="w-3 h-3 transition-transform duration-200 group-hover:rotate-180" />
                  </span>
                )}
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-all duration-200 z-50">
                  <div className="bg-ivory text-ink shadow-[0_24px_60px_-20px_rgba(0,0,0,0.45)] border border-ink/10 py-2 min-w-[230px]">
                    {m.children.map((c, j) => (
                      <a
                        key={j}
                        href={c.url}
                        className="block px-6 py-2.5 text-[11px] uppercase tracking-[0.18em] text-ink hover:bg-ink/5 hover:text-clay transition-colors whitespace-nowrap"
                        data-testid={`nav-sublink-${i}-${j}`}
                      >
                        {c.label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>
      </div>

      {/* Mobile menu drawer */}
      {open && (
        <div className="lg:hidden bg-ivory border-t border-ink/10" data-testid="mobile-menu">
          <div className="container-x py-8 flex flex-col gap-6">
            {menu.map((m, i) => (
              <div key={i}>
                <div className="flex items-center justify-between">
                  {m.url ? (
                    <a href={m.url} className="text-base font-medium text-ink" data-testid={`mobile-nav-${i}`}>
                      {m.label}
                    </a>
                  ) : (
                    <button
                      onClick={() => setExpanded((e) => ({ ...e, [i]: !e[i] }))}
                      className="text-base font-medium text-ink"
                      data-testid={`mobile-nav-${i}`}
                    >
                      {m.label}
                    </button>
                  )}
                  {m.children?.length > 0 && (
                    <button
                      onClick={() => setExpanded((e) => ({ ...e, [i]: !e[i] }))}
                      className="p-2 -mr-2 text-ink/60"
                      aria-label={`Expand ${m.label}`}
                      data-testid={`mobile-nav-expand-${i}`}
                    >
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expanded[i] ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
                {m.children?.length > 0 && expanded[i] && (
                  <div className="mt-3 ml-4 pl-4 border-l border-ink/15 flex flex-col gap-3">
                    {m.children.map((c, j) => (
                      <a key={j} href={c.url} className="text-sm text-ink/80" data-testid={`mobile-nav-sublink-${i}-${j}`}>
                        {c.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <button
              onClick={() => { setOpen(false); onEnquire(); }}
              className="mt-2 self-start btn-primary"
              data-testid="mobile-enquire-btn"
            >
              Plan a visit
            </button>
            {settings?.contact?.phone && (
              <a
                href={`tel:${settings.contact.phone.replace(/\s+/g, "")}`}
                onClick={() => trackEvent("call_click", { location: "mobile_menu" })}
                className="inline-flex items-center gap-2.5 text-base font-medium text-ink"
                data-testid="mobile-call-btn"
              >
                <span className="w-9 h-9 rounded-full bg-ink text-ivory flex items-center justify-center"><Phone className="w-4 h-4" /></span>
                Call us — {settings.contact.phone}
              </a>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
